// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes — types for the Open Quote DETAIL view.
// Mirrors `Application/Quotes/QuoteDetailModels.cs`; port of legacy ALPACA
// `CRMQuotes_DetailView` (Model.xafml :11543).
// ─────────────────────────────────────────────────────────────────────────────

/** One quote header. Field groups follow the legacy layout. */
export interface QuoteDetail {
  oid: string;

  // Summary
  quoteID: string | null;
  title: string | null;
  /** Decides which of Client / ProspectClient the form shows. */
  isProspect: boolean;
  clientId: string | null;
  clientName: string | null;
  prospectClientId: string | null;
  prospectClientName: string | null;
  tatId: string | null;
  tatName: string | null;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  expirationDate: string | null;
  cancel: boolean;
  cancelReason: string | null;
  dateCanceled: string | null;
  projectId: string | null;
  projectCode: string | null;
  projectName: string | null;
  quotedDate: string | null;
  quotedById: string | null;
  quotedByName: string | null;
  /** Read-only — derived from the rows. Legacy caption "Amount". */
  quotedAmount: number;
  remark: string | null;

  // Totals — all derived from the two row sets
  detailedAmount: number;
  discountAmount: number;
  /** Legacy caption "Discount %". */
  discount: number;
  totalAmount: number;

  // Contacts
  primaryContactId: string | null;
  primaryContactName: string | null;
  emailID: string | null;
  cellPhone: string | null;
  officePhone: string | null;

  // Bill To Address
  billStreet1: string | null;
  billStreet2: string | null;
  billCity: string | null;
  billState: string | null;
  billZipCode: string | null;
  billCountry: string | null;

  // Report to Address
  sameAddress: boolean;
  shipStreet1: string | null;
  shipStreet2: string | null;
  shipCity: string | null;
  shipState: string | null;
  shipZipCode: string | null;
  shipCountry: string | null;

  // Account Management
  ownerId: string | null;
  ownerName: string | null;
  creditId: string | null;
  creditName: string | null;
  sourceOpportunityId: string | null;
  sourceOpportunityName: string | null;

  /** The status as stored. */
  storedStatus: number | null;
  /** The status as displayed — a lapsed quote reads Expired whatever is stored. */
  statusCode: number | null;
  status: string | null;

  divisionId: string | null;
  division: string | null;

  /** Sample registration already points at this quote, so it cannot be deleted. */
  isUsedInRegistration: boolean;
  /** Round-tripped on save so a stale copy is refused, not silently applied. */
  lockVersion: number;
}

/**
 * One Analysis Pricing row. Only `testDescription`, `qty`, `discount`, `remark`
 * and `sort` are editable; everything else comes from the price code.
 */
export interface QuoteAnalysisRow {
  oid: string;
  priceCode: string | null;
  testDescription: string | null;
  matrix: string | null;
  testId: string | null;
  test: string | null;
  isGroup: boolean;
  method: string | null;
  sampleMatrixId: string | null;
  sampleMatrix: string | null;
  componentId: string | null;
  component: string | null;
  parameter: string | null;
  /** 0 Test · 1 Parameter. */
  chargeTypeCode: number;
  tat: string | null;
  priority: string | null;
  qty: number;
  unitPrice: number;
  tierNo: number;
  tierFrom: number;
  tierTo: number;
  /** The price of ONE unit — what Discount and Qty are applied to. */
  totalTierPrice: number;
  /** Legacy caption "Discount(%)". */
  discount: number;
  discountAmount: number;
  finalAmount: number;
  remark: string | null;
  sort: number;
  /** Set only on a row not stored yet — sent back so the save can price it. */
  priceCodeId: string | null;
  isNew: boolean;
}

/** One Item Charge row. */
export interface QuoteItemRow {
  oid: string;
  itemPriceId: string | null;
  itemCode: string | null;
  itemName: string | null;
  category: string | null;
  unit: string | null;
  unitPrice: number;
  qty: number;
  /** The LINE amount: unit price × quantity, before discount. */
  amount: number;
  discount: number;
  finalAmount: number;
  remark: string | null;
  isNew: boolean;
}

/** The five header amounts, all derived from the rows by the server. */
export interface QuoteTotals {
  detailedAmount: number;
  discountAmount: number;
  discount: number;
  totalAmount: number;
  quotedAmount: number;
}

/** One row of the "add price code" popup. */
export interface PriceCodeCandidate {
  candidateKey: string;
  priceCodeId: string | null;
  priceCode: string | null;
  /** False = the test has no price code and cannot be added. */
  hasPriceCode: boolean;
  sampleMatrixId: string;
  sampleMatrix: string | null;
  matrix: string | null;
  testId: string | null;
  test: string | null;
  isGroup: boolean;
  method: string | null;
  componentId: string | null;
  component: string | null;
  chargeTypeCode: number;
  unitPrice: number | null;
}

/** One row of the "add item charge" popup. */
export interface ItemChargeCandidate {
  itemPriceId: string;
  itemCode: string | null;
  itemName: string | null;
  description: string | null;
  category: string | null;
  unit: string | null;
  unitPrice: number;
}

export interface LookupOption { id: string; name: string | null }

/** A client or prospect, with the address Bill To is pre-filled from. */
export interface PartyOption extends LookupOption {
  street1: string | null;
  street2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
  /** Prospects only. */
  primaryContactId: string | null;
}

export interface ProjectOption extends LookupOption { projectName: string | null }

export interface ContactOption extends LookupOption {
  email: string | null;
  mobilePhone: string | null;
  officePhone: string | null;
  otherPhone: string | null;
}

export interface SampleMatrixOption extends LookupOption { matrix: string | null }

/** Every header dropdown that does not depend on another field. */
export interface QuoteLookups {
  clients: PartyOption[];
  prospects: PartyOption[];
  tats: LookupOption[];
  paymentMethods: LookupOption[];
  credits: LookupOption[];
  opportunities: LookupOption[];
  employees: LookupOption[];
  sampleMatrices: SampleMatrixOption[];
}

/** One analysis row as sent to the server. Prices are never sent. */
export interface AnalysisRowInput {
  oid: string;
  priceCodeId?: string | null;
  sampleMatrixId?: string | null;
  qty: number;
  discount: number;
  testDescription?: string | null;
  remark?: string | null;
  sort: number;
}

/** One item row as sent to the server. */
export interface ItemRowInput {
  oid: string;
  itemPriceId?: string | null;
  qty: number;
  discount: number;
  /** Set ONLY when the user typed a Final Amount. */
  finalAmountOverride?: number | null;
  remark?: string | null;
}

/** The quote as sent to the server: header fields + the COMPLETE row sets. */
export interface QuoteInput {
  lockVersion?: number | null;
  title: string | null;
  isProspect: boolean;
  clientId: string | null;
  prospectClientId: string | null;
  tatId: string | null;
  paymentMethodId: string | null;
  expirationDate: string | null;
  cancel: boolean;
  cancelReason: string | null;
  projectId: string | null;
  quotedDate: string | null;
  quotedById: string | null;
  remark: string | null;
  primaryContactId: string | null;
  emailID: string | null;
  cellPhone: string | null;
  officePhone: string | null;
  billStreet1: string | null;
  billStreet2: string | null;
  billCity: string | null;
  billState: string | null;
  billZipCode: string | null;
  billCountry: string | null;
  sameAddress: boolean;
  shipStreet1: string | null;
  shipStreet2: string | null;
  shipCity: string | null;
  shipState: string | null;
  shipZipCode: string | null;
  shipCountry: string | null;
  ownerId: string | null;
  creditId: string | null;
  sourceOpportunityId: string | null;
  rows: AnalysisRowInput[];
  items: ItemRowInput[];
}

/** Legacy `ChargeType` ordinals (Hr/Enum.cs). */
export const CHARGE_TYPE: Record<number, string> = { 0: 'Test', 1: 'Parameter' };

/** A new, empty quote. Quoted By / Owner / Division are stamped by the server
 *  from the signed-in user on the first save, as legacy AfterConstruction does. */
export function blankQuote(): QuoteDetail {
  return {
    oid: '', quoteID: null, title: null, isProspect: false,
    clientId: null, clientName: null, prospectClientId: null, prospectClientName: null,
    tatId: null, tatName: null, paymentMethodId: null, paymentMethodName: null,
    expirationDate: null, cancel: false, cancelReason: null, dateCanceled: null,
    projectId: null, projectCode: null, projectName: null,
    quotedDate: null, quotedById: null, quotedByName: null, quotedAmount: 0, remark: null,
    detailedAmount: 0, discountAmount: 0, discount: 0, totalAmount: 0,
    primaryContactId: null, primaryContactName: null, emailID: null, cellPhone: null, officePhone: null,
    billStreet1: null, billStreet2: null, billCity: null, billState: null, billZipCode: null, billCountry: null,
    sameAddress: false,
    shipStreet1: null, shipStreet2: null, shipCity: null, shipState: null, shipZipCode: null, shipCountry: null,
    ownerId: null, ownerName: null, creditId: null, creditName: null,
    sourceOpportunityId: null, sourceOpportunityName: null,
    storedStatus: null, statusCode: null, status: null,
    divisionId: null, division: null, isUsedInRegistration: false, lockVersion: 0,
  };
}
